import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import type { CalendarPlanSlot } from "@/types/contentCalendar.type";
import { displayAspectRatio } from "@/types/contentCalendar.type";
import {
  distributionFiles,
  distributionPublications,
  type DeliverableThumbnailTicket,
  type DeliverableUploadTicket,
  type DistributionResult,
  type SlotDeliverable,
} from "@/types/scheduling.type";
import { brandLocalToUtc, MIN_LEAD_MS } from "@/lib/brandTime";
import {
  distributionKey,
  useClearLivePostLink,
  useDeleteSlotFile,
  useDistribution,
  usePublishNowPublication,
  useRefreshLivePost,
  useRetryPublication,
  useSchedulePublication,
  useSetLivePostLink,
  useSetSlotItemFile,
  useSetSlotItems,
  useUnschedulePublication,
  useUpdateChannelCopy,
  useUpdatePublication,
} from "../api/useScheduling";
import { reconcileAllPlans } from "../api/calendarCache";
import { putToPresignedUrl } from "../api/putToPresignedUrl";
import { intendedPublicationChannels } from "../publicationIntegrity";
import { acceptedIdea } from "../slotStage";
import { captureVideoThumbnails } from "../videoThumbnails";
import { fieldsForCaption, liveFromDistribution, PLATFORM_NAME as PLATFORM_LABEL, ratioOf, type LiveSource } from "./liveModel";
import { LIVE_ACCEPT, LIVE_ACCEPT_IMAGES, type LiveStageHandle } from "./liveStage";
import type { LiveData, LiveFile } from "./liveTypes";

/** The most files one post holds (backend MAX_SLOT_FILES). */
const MAX_POST_FILES = 40;
const PARALLEL_UPLOADS = 3;
/** How long a removed file can still be brought back (every calendar Undo is eight seconds). */
const REMOVE_UNDO_MS = 8_000;

/** A file picked in this tab: uploading, failed, or confirmed and waiting for the match. */
interface LocalFile {
  id: string;
  /** The post it was picked on. An upload keeps going for its own post after the page moves on. */
  slotId: string;
  file: File;
  meta: LiveFile;
  /** The server's id once confirmed. The row goes when the match lands. */
  serverId?: string | null;
  /** The carousel slide it goes on (dropped there, or the first empty one). */
  itemId?: string;
  /**
   * Where the server will put it, worked out when it was added, so the file
   * sits in that section from the first frame instead of moving once the
   * match lands: on a slide or a channel ("placed"), or in the tray.
   */
  predicted: { placed: boolean; note?: string };
}

/** One post's uploads since its last match: the match runs once none is left running. */
interface Batch {
  running: number;
  confirmed: string[];
  failed: string[];
}

let localSeq = 0;

/** Runs at most `size` tasks at once, the rest in the order they came. */
function createLimiter(size: number) {
  let active = 0;
  const waiting: Array<() => void> = [];
  const next = () => {
    if (active >= size) return;
    const run = waiting.shift();
    if (!run) return;
    active += 1;
    run();
  };
  return <T>(task: () => Promise<T>) =>
    new Promise<T>((resolve, reject) => {
      waiting.push(() => {
        task()
          .then(resolve, reject)
          .finally(() => {
            active -= 1;
            next();
          });
      });
      next();
    });
}

const limitUpload = createLimiter(PARALLEL_UPLOADS);

const stopped = () => new DOMException("The upload was stopped", "AbortError");

function readMeta(file: File): Promise<LiveFile> {
  const id = `local-${Date.now()}-${(localSeq += 1)}`;
  const url = URL.createObjectURL(file);
  const video = file.type.startsWith("video/");
  const base: LiveFile = {
    id,
    name: file.name,
    kind: video ? "VIDEO" : "IMAGE",
    ratio: null,
    durationSeconds: null,
    thumbUrl: video ? null : url,
    mediaUrl: url,
    status: "ready",
  };
  return new Promise((resolve) => {
    const done = (width: number, height: number, duration: number | null) =>
      resolve({ ...base, ratio: ratioOf(width, height), durationSeconds: duration });
    if (video) {
      const element = document.createElement("video");
      element.preload = "metadata";
      element.muted = true;
      element.onloadedmetadata = () => done(element.videoWidth, element.videoHeight, Number.isFinite(element.duration) ? element.duration : null);
      element.onerror = () => resolve(base);
      element.src = url;
    } else {
      const image = new Image();
      image.onload = () => done(image.naturalWidth, image.naturalHeight, null);
      image.onerror = () => resolve(base);
      image.src = url;
    }
  });
}

function errorText(error: unknown): string {
  if (isAxiosError(error)) return (error.response?.data as { message?: string } | undefined)?.message ?? error.message;
  return error instanceof Error ? error.message : "Try again";
}

/*
 * The upload's three calls, with the post they belong to passed in rather than
 * read from a hook. A mutation hook takes the options of the latest render, so
 * after the page moved to another post its confirm would land on that post.
 */
async function requestUploadUrl(slotId: string, file: File) {
  const res = await axiosInstance.post<DeliverableUploadTicket>(`/scheduling/slots/${slotId}/deliverable/upload-url`, {
    filename: file.name,
    mimetype: file.type,
    extension: file.name.split(".").pop() ?? "",
    size: file.size,
  });
  return res.data;
}

async function requestThumbnailUrl(slotId: string, input: { mimetype: string; extension: string; size: number }) {
  const res = await axiosInstance.post<DeliverableThumbnailTicket>(`/scheduling/slots/${slotId}/deliverable/thumbnail/upload-url`, input);
  return res.data;
}

async function confirmDeliverable(slotId: string, input: { uploadToken: string; thumbnailToken?: string; itemId?: string }) {
  const res = await axiosInstance.post<{ deliverable: SlotDeliverable }>(`/scheduling/slots/${slotId}/deliverable/confirm`, input);
  return res.data.deliverable;
}

async function matchSlotFiles(slotId: string) {
  const res = await axiosInstance.post<DistributionResult>(`/scheduling/slots/${slotId}/files/match`);
  return res.data;
}

async function setPlatformFile(slotId: string, platform: string, deliverableId: string) {
  const res = await axiosInstance.put<DistributionResult>(`/scheduling/slots/${slotId}/platforms/${platform}/file`, { deliverableId });
  return res.data;
}

async function setPlatformCover(slotId: string, platform: string, coverDeliverableId: string | null) {
  const res = await axiosInstance.put<DistributionResult>(`/scheduling/slots/${slotId}/platforms/${platform}/cover`, { coverDeliverableId });
  return res.data;
}

const fileCount = (n: number) => `${n} ${n === 1 ? "file" : "files"}`;

/**
 * Where each picked file will land, worked out before it uploads so the page
 * shows it there from the start (the server follows the same rules):
 *
 *  - a picture on a carousel goes on the slide it was dropped on, then on the
 *    empty slides after it (from the first, when picked), skipping slides other
 *    uploads already claimed; with no empty slide left it waits in the tray;
 *  - a file of a single-picture or video post becomes the post's file (the
 *    newest upload wins every platform);
 *  - a picture on a video post is a cover while a channel that takes one still
 *    has none, else it waits in the tray.
 */
function predictPlacement(
  base: LiveData,
  inFlight: LocalFile[],
  files: File[],
  droppedOn?: string,
): Array<{ itemId?: string; predicted: LocalFile["predicted"] }> {
  const slides = base.slides?.items ?? [];
  const carousel = slides.length >= 2;
  const claimed = new Set(inFlight.filter((entry) => entry.meta.status !== "failed" && entry.itemId).map((entry) => entry.itemId!));
  const from = droppedOn ? Math.max(0, slides.findIndex((item) => item.id === droppedOn)) : 0;
  const free = slides
    .slice(from)
    .filter((item, index) => (droppedOn && index === 0) || (!item.fileId && !claimed.has(item.id)))
    .map((item) => item.id);
  const videoPost = base.channels.some((channel) => channel.kind === "VIDEO");
  let coversOpen = base.channels.filter(
    (channel) =>
      channel.kind === "VIDEO" &&
      channel.wantsCover &&
      !base.matches.some((match) => match.role === "cover" && match.channelIds.includes(channel.id) && !match.fileId.endsWith(":cover")),
  ).length;
  return files.map((file) => {
    const picture = file.type.startsWith("image/");
    if (carousel && picture) {
      const itemId = free.shift();
      return itemId
        ? { itemId, predicted: { placed: true } }
        : { predicted: { placed: false, note: "no empty slide left; drop it on a slide" } };
    }
    if (videoPost && picture) {
      if (coversOpen > 0) {
        coversOpen = 0; // one picture covers every channel still without one
        return { predicted: { placed: true } };
      }
      return { predicted: { placed: false, note: "another picture is the cover" } };
    }
    return { predicted: { placed: true } };
  });
}

/**
 * The Live tab on the scheduling API (api/useScheduling.ts). Up to 20 files
 * per post, picked or dropped together: each goes up through the presigned
 * flow (a video with its first frame as the thumbnail) and is confirmed, a few
 * at a time. Once none of the post's uploads is left running, the server
 * matches every file to the post's platforms in one call (POST /files/match):
 * the best file for each, and on a video post the best picture as its cover.
 * A file that went to no channel waits in the not-placed tray. Removing a file
 * deletes it on the server, which takes it off every platform it was on. An
 * upload finishes for its own post when the page moves to another one; leaving
 * the page stops what is still uploading, says so, and still matches what got
 * in.
 */
export function useLiveStage({
  slot,
  enabled,
  timeZone,
}: {
  slot: CalendarPlanSlot | null;
  enabled: boolean;
  timeZone: string;
}): LiveStageHandle | null {
  const slotId = slot?.id ?? null;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const distribution = useDistribution(slotId, enabled && !!slotId);
  const update = useUpdatePublication(slotId);
  const setLink = useSetLivePostLink(slotId);
  const clearLink = useClearLivePostLink(slotId);
  const refresh = useRefreshLivePost(slotId);
  // The editor waits on these (its loading states), so none writes the cache first.
  const scheduleOne = useSchedulePublication(slotId, { optimistic: false });
  const unscheduleOne = useUnschedulePublication(slotId, { optimistic: false });
  const retryOne = useRetryPublication(slotId);
  const postNowOne = usePublishNowPublication(slotId, { optimistic: false });
  const deleteFile = useDeleteSlotFile(slotId);
  const saveCopy = useUpdateChannelCopy(slotId);
  const setItems = useSetSlotItems(slotId);
  const setItemFile = useSetSlotItemFile(slotId);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [local, setLocal] = useState<LocalFile[]>([]);
  const aborts = useRef(new Map<string, { controller: AbortController; name: string }>());
  const batches = useRef(new Map<string, Batch>());
  // Local rows the person removed: one whose confirm was already on its way is deleted again.
  const removed = useRef(new Set<string>());

  // Another post starts clean: its failed rows are the old post's. Uploads
  // still running for the old post keep their rows (hidden here) until they
  // land.
  const [seenSlot, setSeenSlot] = useState(slotId);
  if (seenSlot !== slotId) {
    setSeenSlot(slotId);
    setLocal((previous) => previous.filter((entry) => entry.meta.status !== "failed"));
  }

  // Object URLs go when their row goes.
  const urls = useRef(new Set<string>());
  useEffect(() => {
    const live = new Set(local.map((entry) => entry.meta.mediaUrl).filter((url): url is string => !!url?.startsWith("blob:")));
    // One set, changed in place, so the unmount below sees the latest.
    for (const url of urls.current) {
      if (live.has(url)) continue;
      URL.revokeObjectURL(url);
      urls.current.delete(url);
    }
    for (const url of live) urls.current.add(url);
  }, [local]);

  // Leaving the page stops what is still uploading, and says so once. What
  // already got in is still matched when the last stopped upload settles.
  useEffect(() => {
    const running = aborts.current;
    const held = urls.current;
    return () => {
      const halted = [...running.values()];
      for (const { controller } of halted) controller.abort();
      if (halted.length) {
        queue.add({
          title: halted.length === 1 ? `The upload of ${halted[0].name} was stopped` : `${halted.length} uploads were stopped`,
          description: halted.length === 1 ? "Add it again on the Live tab." : "Add them again on the Live tab.",
          variant: "error",
        });
      }
      running.clear();
      for (const url of held) URL.revokeObjectURL(url);
    };
  }, []);

  const source: LiveSource | null = useMemo(() => {
    if (!slot) return null;
    const idea = acceptedIdea(slot);
    // Captions come from the post's own copy (distribution.channelCopy), seeded
    // from the brief when it was accepted; the brief is not read here.
    return {
      slotId: slot.id,
      mediaType: slot.mediaType,
      ratio: displayAspectRatio(slot),
      lengthSeconds: slot.mediaType === "STATIC" ? null : (idea?.durationSeconds ?? null),
      channels: intendedPublicationChannels(slot),
      distribution: distribution.data,
    };
  }, [slot, distribution.data]);

  const baseAll = useMemo(() => (source ? liveFromDistribution(source) : null), [source]);
  const slotKind = slot?.mediaType === "STATIC" ? "IMAGE" : "VIDEO";

  // A removed file leaves the pile at once and the delete waits out its Undo
  // (Figma 08, "a file removed, with Undo"): the server deletes for good, so
  // the eight seconds are the only way back.
  const [hiddenIds, setHiddenIds] = useState<ReadonlySet<string>>(() => new Set());
  const holdTimers = useRef(new Map<string, number>());
  const unhide = useCallback((fileId: string) => {
    setHiddenIds((previous) => {
      if (!previous.has(fileId)) return previous;
      const next = new Set(previous);
      next.delete(fileId);
      return next;
    });
  }, []);
  const holdRemoval = useCallback(
    (fileId: string, name: string) => {
      setHiddenIds((previous) => new Set(previous).add(fileId));
      const timer = window.setTimeout(() => {
        holdTimers.current.delete(fileId);
        deleteFile.mutate(fileId, { onSettled: () => unhide(fileId) });
      }, REMOVE_UNDO_MS);
      holdTimers.current.set(fileId, timer);
      queue.add({
        title: `${name} removed.`,
        description: "The channels it was on need a file again.",
        variant: "success",
        action: {
          label: "Undo",
          onPress: () => {
            const held = holdTimers.current.get(fileId);
            if (held === undefined) return;
            window.clearTimeout(held);
            holdTimers.current.delete(fileId);
            unhide(fileId);
          },
        },
        countdown: REMOVE_UNDO_MS / 1000,
      });
    },
    [deleteFile, unhide],
  );
  // Leaving the page keeps a held delete on its way: the toast was the promise.
  useEffect(() => {
    const timers = holdTimers.current;
    return () => {
      for (const [fileId, timer] of timers) {
        window.clearTimeout(timer);
        deleteFile.mutate(fileId);
      }
      timers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const base = useMemo(() => {
    if (!baseAll || hiddenIds.size === 0) return baseAll;
    return {
      ...baseAll,
      files: baseAll.files.filter((file) => !hiddenIds.has(file.id)),
      matches: baseAll.matches.filter((match) => !hiddenIds.has(match.fileId)),
      unplaced: baseAll.unplaced.filter((entry) => !hiddenIds.has(entry.fileId)),
      ...(baseAll.slides
        ? { slides: { ...baseAll.slides, items: baseAll.slides.items.map((item) => (item.fileId && hiddenIds.has(item.fileId) ? { ...item, fileId: null } : item)) } }
        : {}),
    };
  }, [baseAll, hiddenIds]);

  const patch = useCallback((id: string, change: Partial<LocalFile> | ((entry: LocalFile) => Partial<LocalFile>)) => {
    setLocal((previous) => previous.map((entry) => (entry.id === id ? { ...entry, ...(typeof change === "function" ? change(entry) : change) } : entry)));
  }, []);
  const drop = useCallback((id: string) => {
    aborts.current.get(id)?.controller.abort();
    aborts.current.delete(id);
    setLocal((previous) => previous.filter((entry) => entry.id !== id));
  }, []);

  /**
   * Match every file on the post to its platforms, once. The confirmed rows
   * then give way to the server's files; on a failure they still do (the files
   * are on the post) and the toast offers the match again.
   */
  const runMatch = useCallback(
    async (target: string, confirmed: string[]) => {
      try {
        const result = await matchSlotFiles(target);
        // A poll already on its way would put the read from before the match back.
        await qc.cancelQueries({ queryKey: distributionKey(target) });
        qc.setQueryData(distributionKey(target), result);
      } catch (error) {
        queue.add({
          title: "The files could not be matched to the channels",
          description: errorText(error),
          variant: "error",
          action: { label: "Try again", onPress: () => void runMatch(target, []) },
        });
        await qc.refetchQueries({ queryKey: distributionKey(target) });
      } finally {
        reconcileAllPlans(qc);
        void qc.invalidateQueries({ queryKey: ["calendarPlans"] });
        const done = new Set(confirmed);
        setLocal((previous) => previous.filter((entry) => !done.has(entry.id)));
      }
    },
    [qc],
  );

  /** Upload one file and confirm it, a few at a time. The post's last upload to settle starts the match. */
  const upload = useCallback(
    async (entry: LocalFile, fallbackCover: Blob | null) => {
      const target = entry.slotId;
      const controller = new AbortController();
      aborts.current.set(entry.id, { controller, name: entry.meta.name });
      const batch = batches.current.get(target) ?? { running: 0, confirmed: [], failed: [] };
      batches.current.set(target, batch);
      batch.running += 1;
      try {
        await limitUpload(async () => {
          const step = () => {
            if (controller.signal.aborted) throw stopped();
          };
          step();
          const ticket = await requestUploadUrl(target, entry.file);
          step();
          await putToPresignedUrl(ticket.uploadUrl, entry.file, entry.file.type, {
            signal: controller.signal,
            onProgress: (progress) => patch(entry.id, (current) => ({ meta: { ...current.meta, progress } })),
          });
          let thumbnailToken: string | undefined;
          if (entry.meta.kind === "VIDEO") {
            // The first frame the browser can draw, else a picture from the same drop.
            const frames = await captureVideoThumbnails(entry.file, [0.05]).catch(() => []);
            const blob: Blob | null = frames[0]?.blob ?? fallbackCover;
            for (const frame of frames) URL.revokeObjectURL(frame.previewUrl);
            if (!blob) throw new Error("No cover could be drawn from this video. Add a picture with it.");
            step();
            const type = blob.type || "image/jpeg";
            const thumbTicket = await requestThumbnailUrl(target, { mimetype: type, extension: type.split("/")[1] ?? "jpg", size: blob.size });
            await putToPresignedUrl(thumbTicket.uploadUrl, blob, type, { signal: controller.signal });
            thumbnailToken = thumbTicket.thumbnailToken;
          }
          // A picture's smaller thumbnail is rendered on the server (the
          // resize-image Lambda) once it is confirmed; the original shows until then.
          step();
          const deliverable = await confirmDeliverable(target, {
            uploadToken: ticket.uploadToken,
            thumbnailToken,
            ...(entry.itemId ? { itemId: entry.itemId } : {}),
          });
          if (removed.current.has(entry.id)) {
            await axiosInstance.delete(`/scheduling/slots/${target}/files/${deliverable.id}`).catch(() => undefined);
            throw stopped();
          }
          batch.confirmed.push(entry.id);
          patch(entry.id, (current) => ({ serverId: deliverable.id, meta: { ...current.meta, status: "processing", progress: null } }));
        });
      } catch (error) {
        // Removed, or stopped by leaving the page (that cleanup already said so).
        if (error instanceof DOMException && error.name === "AbortError") return;
        batch.failed.push(entry.meta.name);
        patch(entry.id, (current) => ({ meta: { ...current.meta, status: "failed", progress: null, error: errorText(error) } }));
      } finally {
        aborts.current.delete(entry.id);
        removed.current.delete(entry.id);
        batch.running -= 1;
        if (batch.running === 0) {
          batches.current.delete(target);
          if (batch.failed.length) {
            queue.add({
              title: batch.failed.length === 1 ? `The upload of ${batch.failed[0]} failed` : `${batch.failed.length} uploads failed`,
              description: "The reason is on each file. Remove it, then add it again.",
              variant: "error",
            });
          }
          if (batch.confirmed.length) void runMatch(target, batch.confirmed);
        }
      }
    },
    [patch, runMatch],
  );

  const addFiles = useCallback(
    async (picked: File[], itemId?: string) => {
      if (!base || !slotId) return;
      // A video post takes videos and pictures (a picture is a cover); a static, pictures.
      const fits = (file: File) => file.type.startsWith("image/") || (slotKind === "VIDEO" && file.type.startsWith("video/"));
      const wrongKind = picked.filter((file) => !fits(file));
      if (wrongKind.length) {
        queue.add({
          title: `${fileCount(wrongKind.length)} left out`,
          description: slotKind === "VIDEO" ? "This post takes videos and pictures." : "This post takes pictures only.",
          variant: "error",
        });
      }
      const onServer = new Set(distributionFiles(distribution.data).map((file) => file.id));
      const pending = local.filter(
        (entry) => entry.slotId === slotId && entry.meta.status !== "failed" && !(entry.serverId && onServer.has(entry.serverId)),
      ).length;
      const room = Math.max(0, MAX_POST_FILES - onServer.size - pending);
      const accepted = picked.filter(fits).slice(0, room);
      const over = picked.filter(fits).length - accepted.length;
      if (over > 0) {
        queue.add({
          title: `A post holds up to ${MAX_POST_FILES} files`,
          description: `${fileCount(over)} left out. Remove a file to add another.`,
          variant: "error",
        });
      }
      if (!accepted.length) return;
      const metas = await Promise.all(accepted.map(readMeta));
      const predict = predictPlacement(base, local.filter((entry) => entry.slotId === slotId), accepted, itemId);
      const added: LocalFile[] = metas.map((meta, index) => ({
        id: meta.id,
        slotId,
        file: accepted[index],
        meta: { ...meta, status: "uploading", progress: 0, error: null },
        ...(predict[index].itemId ? { itemId: predict[index].itemId } : {}),
        predicted: predict[index].predicted,
      }));
      setLocal((previous) => [...previous, ...added]);
      const fallbackCover = accepted.find((file) => file.type.startsWith("image/")) ?? null;
      for (const entry of added) void upload(entry, fallbackCover);
    },
    [base, slotId, slotKind, local, distribution.data, upload],
  );

  const handle = useMemo<LiveStageHandle | null>(() => {
    if (!base || !slotId) return null;
    const mine = local.filter((entry) => entry.slotId === slotId);
    const onServer = new Set(base.files.map((file) => file.id));
    // A confirmed file shows as the server's once the read carries it. Until
    // its match lands, every upload stays where it was predicted to go: on its
    // slide or channel, or in the tray, never moving between the two.
    const shown = mine.filter((entry) => !(entry.serverId && onServer.has(entry.serverId)));
    const live = mine.filter((entry) => entry.meta.status !== "failed");
    const idOf = (entry: LocalFile) => (entry.serverId && onServer.has(entry.serverId) ? entry.serverId : entry.meta.id);
    const predictedPlaced = new Set(live.filter((entry) => entry.predicted.placed).map(idOf));
    const predictedTray = live.filter((entry) => !entry.predicted.placed);
    // A picture going on a slide shows there at once, while it uploads.
    const onSlide = new Map(live.filter((entry) => entry.itemId).map((entry) => [entry.itemId!, idOf(entry)]));
    const carouselIds = base.channels.filter((channel) => channel.kind === "CAROUSEL").map((channel) => channel.id);
    const slideMatches = base.slides
      ? base.slides.items.flatMap((item, index) => {
          const fileId = onSlide.get(item.id);
          return fileId && carouselIds.length && !base.matches.some((match) => match.fileId === fileId)
            ? [{ fileId, channelIds: carouselIds, role: "slide" as const, slide: index + 1 }]
            : [];
        })
      : [];
    const trayNote = (entry: LocalFile) =>
      entry.meta.status === "uploading"
        ? `uploading, ${entry.meta.progress ?? 0}%`
        : entry.meta.status === "processing"
          ? "checking the file"
          : (entry.predicted.note ?? "not placed");
    const data: LiveData = {
      ...base,
      files: [...base.files, ...shown.map((entry) => entry.meta)],
      matches: [...base.matches, ...slideMatches],
      unplaced: [
        ...base.unplaced.filter((entry) => !predictedPlaced.has(entry.fileId)),
        ...predictedTray
          .filter((entry) => !base.unplaced.some((row) => row.fileId === idOf(entry)))
          .map((entry) => ({ fileId: idOf(entry), note: trayNote(entry) })),
      ],
      ...(base.slides && onSlide.size
        ? {
            slides: {
              ...base.slides,
              items: base.slides.items.map((item) => (onSlide.has(item.id) ? { ...item, fileId: onSlide.get(item.id)! } : item)),
            },
          }
        : {}),
    };
    const refuse = (title: string, description: string) => queue.add({ title, description, variant: "error" });
    // A file on a channel that is scheduled, posting or live stays (the server refuses too).
    const lockedChannels = new Set(
      data.posts
        .filter((post) => post.liveSince || post.route?.state === "scheduled" || post.route?.state === "posting")
        .map((post) => post.channelId),
    );
    const lockedFiles = new Set(
      base.matches.filter((match) => match.channelIds.some((id) => lockedChannels.has(id))).map((match) => match.fileId),
    );

    // Publishing (the scheduling API): each channel's publication goes out
    // from CreativeOS at its time, or waits to be posted by hand.
    const publications = distributionPublications(distribution.data);
    const publicationOf = (channelId: string) => {
      const id = data.posts.find((item) => item.channelId === channelId)?.publicationId;
      return publications.find((item) => item.id === id) ?? null;
    };
    const nameOf = (channelId: string) => {
      const channel = data.channels.find((item) => item.id === channelId);
      return channel ? (PLATFORM_LABEL[channel.platform] ?? channel.platform) : "The post";
    };
    // The post's copy for a platform, as the server keeps it (SlotChannel).
    const copyOf = (platform: string) =>
      distribution.data?.channelCopy?.find((row) => row.platform === platform)?.copy ??
      publications.find((item) => item.platform === platform)?.fields ??
      {};
    const tooSoon = (iso: string | null) => !!iso && new Date(iso).getTime() < Date.now() + MIN_LEAD_MS;
    // The editor waits on each change: it resolves once the read carries it,
    // and rejects (after the toast) when it was refused.
    const settle = () => qc.refetchQueries({ queryKey: distributionKey(slotId) });
    const refused = (title: string, description: string) => {
      refuse(title, description);
      return Promise.reject(new Error(title));
    };
    const publishing: Partial<LiveStageHandle> = {
      connect: () => navigate("/dashboard/settings?page=connections"),
      schedule: async (channelId) => {
        const publication = publicationOf(channelId);
        if (!publication) return;
        if (tooSoon(publication.scheduledAt)) {
          return refused("Pick a later time first", "A post is scheduled at least 5 minutes ahead.");
        }
        await scheduleOne.mutateAsync(publication.id);
        await settle();
        queue.add(
          { title: `${nameOf(channelId)} is scheduled`, variant: "success", action: { label: "Undo", onPress: () => unscheduleOne.mutate(publication.id) } },
          { timeout: 8000 },
        );
      },
      scheduleAll: () => {
        const ready = publications.filter((item) => item.status === "READY" && !!item.socialAccountId && !tooSoon(item.scheduledAt));
        if (ready.length === 0) return;
        void Promise.allSettled(ready.map((item) => scheduleOne.mutateAsync(item.id))).then((results) => {
          const done = ready.filter((_, index) => results[index].status === "fulfilled");
          if (done.length === 0) return;
          queue.add(
            {
              title: done.length === 1 ? "Scheduled" : `${done.length} posts scheduled`,
              variant: "success",
              action: { label: "Undo", onPress: () => done.forEach((item) => unscheduleOne.mutate(item.id)) },
            },
            { timeout: 8000 },
          );
        });
      },
      unschedule: async (channelId) => {
        const publication = publicationOf(channelId);
        if (!publication) return;
        await unscheduleOne.mutateAsync(publication.id);
        await settle();
        queue.add(
          { title: `${nameOf(channelId)} is off the schedule`, description: "It waits to be posted by hand.", variant: "success", action: { label: "Undo", onPress: () => scheduleOne.mutate(publication.id) } },
          { timeout: 8000 },
        );
      },
      retryPost: (channelId) => {
        const publication = publicationOf(channelId);
        if (publication) retryOne.mutate(publication.id);
      },
      setSelfPost: async (channelId, selfPost) => {
        const publication = publicationOf(channelId);
        if (!publication) return;
        try {
          await axiosInstance.post(`/scheduling/publications/${publication.id}/self-post`, { selfPost });
        } catch (error) {
          return refused(`${nameOf(channelId)} could not be changed`, errorText(error));
        }
        await settle();
      },
      postNow: async (channelId) => {
        const publication = publicationOf(channelId);
        if (!publication) return;
        await postNowOne.mutateAsync(publication.id);
        await settle();
        queue.add({
          title: `${nameOf(channelId)} is posting now`,
          description: "It goes out within a minute.",
          variant: "success",
        });
      },
      setTime: async (channelId, day, time) => {
        const publication = publicationOf(channelId);
        if (!publication) return;
        const utc = brandLocalToUtc(new Date(`${day}T00:00:00Z`), time, timeZone);
        if (utc.getTime() < Date.now() + MIN_LEAD_MS) {
          return refused("Pick a later time", "A post goes out at least 5 minutes from now.");
        }
        const wasScheduled = publication.status === "SCHEDULED";
        await update.mutateAsync({ publicationId: publication.id, input: { scheduledAt: { day, time } } });
        if (wasScheduled) await scheduleOne.mutateAsync(publication.id).catch(() => undefined);
        await settle();
      },
    };
    return {
      data,
      // Every upload in this tab is already a row in `data.files`; `total` is this round's size.
      uploads: { inFlight: 0, total: mine.filter((entry) => entry.meta.status !== "failed").length },
      loading: distribution.isLoading,
      error: distribution.isError,
      retry: () => void distribution.refetch(),
      timeZone,
      registerInput: (input) => {
        inputRef.current = input;
      },
      openPicker: () => inputRef.current?.click(),
      // A static post takes pictures only, so the picker offers no videos.
      accept: slotKind === "IMAGE" ? LIVE_ACCEPT_IMAGES : LIVE_ACCEPT,
      addFiles: (files) => void addFiles(files),
      // A video's own cover goes with its video, never on its own.
      canRemove: (fileId) => !fileId.endsWith(":cover") && !lockedFiles.has(fileId),
      // A file still in this tab stops (and is taken back if it got in); one
      // on the server is deleted there, which takes it off every platform.
      removeFile: (fileId) => {
        if (lockedFiles.has(fileId)) {
          refuse("That file is on a scheduled or live post", "Take the post off the schedule first.");
          return;
        }
        const entry = mine.find((item) => item.id === fileId);
        if (entry) {
          removed.current.add(entry.id);
          drop(entry.id);
          if (entry.serverId) deleteFile.mutate(entry.serverId);
          return;
        }
        if (!onServer.has(fileId) || fileId.endsWith(":cover")) return;
        setLocal((previous) => previous.filter((item) => item.serverId !== fileId));
        holdRemoval(fileId, base.files.find((file) => file.id === fileId)?.name ?? "The file");
      },
      // File and cover: the server's answer is written once it comes, and the
      // editor waits on it (no pick shown before the server has it).
      swapFile: async (channelId, fileId) => {
        const channel = data.channels.find((item) => item.id === channelId);
        if (!channel || !onServer.has(fileId)) return;
        if (lockedChannels.has(channelId)) {
          return refused(`${nameOf(channelId)} is scheduled or live`, "Take it off the schedule to change its file.");
        }
        try {
          const answer = await setPlatformFile(slotId, channel.platform, fileId);
          // A read on its way would put the state from before this back.
          await qc.cancelQueries({ queryKey: distributionKey(slotId) });
          qc.setQueryData(distributionKey(slotId), answer);
        } catch (error) {
          return refused("The file could not be changed", errorText(error));
        } finally {
          reconcileAllPlans(qc);
          void qc.invalidateQueries({ queryKey: ["calendarPlans"] });
        }
      },
      setCover: async (channelId, coverDeliverableId) => {
        const channel = data.channels.find((item) => item.id === channelId);
        if (!channel) return;
        if (coverDeliverableId && !onServer.has(coverDeliverableId)) return;
        if (lockedChannels.has(channelId)) {
          return refused(`${nameOf(channelId)} is scheduled or live`, "Take it off the schedule to change its cover.");
        }
        try {
          const answer = await setPlatformCover(slotId, channel.platform, coverDeliverableId);
          await qc.cancelQueries({ queryKey: distributionKey(slotId) });
          qc.setQueryData(distributionKey(slotId), answer);
        } catch (error) {
          return refused("The cover could not be changed", errorText(error));
        } finally {
          reconcileAllPlans(qc);
          void qc.invalidateQueries({ queryKey: ["calendarPlans"] });
        }
      },
      // Saved on the post (SlotChannel): before any file or account, and on
      // platforms posted by hand. The publication, when there is one, follows.
      saveCaption: async (channelId, text, title) => {
        const post = data.posts.find((item) => item.channelId === channelId);
        const channel = data.channels.find((item) => item.id === channelId);
        if (!post || !channel) return;
        const publication = distributionPublications(distribution.data).find((item) => item.id === post.publicationId);
        const wasScheduled = publication?.status === "SCHEDULED";
        // A refusal rejects (the hook's toast says why) and the editor stays open.
        await saveCopy.mutateAsync({
          platform: channel.platform,
          // Every field the editor does not show is sent back as it was.
          copy: {
            ...copyOf(channel.platform),
            ...fieldsForCaption(channel.platform, text),
            ...(channel.platform === "YOUTUBE" && title !== undefined ? { title } : {}),
          },
        });
        // The server takes an edited post off the schedule; saving keeps it on.
        if (publication && wasScheduled) await scheduleOne.mutateAsync(publication.id).catch(() => undefined);
        await settle();
        queue.add({ title: "Caption saved", description: wasScheduled ? "It stays scheduled." : undefined, variant: "success" });
      },
      setCoverMoment: async (channelId, ms) => {
        const channel = data.channels.find((item) => item.id === channelId);
        if (!channel) return;
        if (lockedChannels.has(channelId) && data.posts.find((post) => post.channelId === channelId)?.liveSince) {
          return refused(`${nameOf(channelId)} is live`, "Its cover went out with it.");
        }
        const publication = publicationOf(channelId);
        const wasScheduled = publication?.status === "SCHEDULED";
        await saveCopy.mutateAsync({ platform: channel.platform, copy: { ...copyOf(channel.platform), coverTimestampMs: ms } });
        if (publication && wasScheduled) await scheduleOne.mutateAsync(publication.id).catch(() => undefined);
        await settle();
      },
      ...(data.slides
        ? {
            reorder: (_channelId: string, from: number, to: number) => {
              const list = data.slides!.items.map((item) => ({ id: item.id, label: item.label }));
              if (from < 1 || to < 1 || from > list.length || to > list.length || from === to) return;
              const [moved] = list.splice(from - 1, 1);
              list.splice(to - 1, 0, moved);
              setItems.mutate(list);
            },
            // One call: a single picture becoming a carousel lands on slide 1
            // on the server too, so the page never shows it leave and come back.
            setSlides: async (entries: { id?: string | null; label: string }[]) => {
              await setItems.mutateAsync(entries);
            },
            placeOnSlide: async (itemId: string, fileId: string | null) => {
              await setItemFile.mutateAsync({ itemId, deliverableId: fileId });
            },
            uploadToSlide: (itemId: string, files: File[]) => void addFiles(files, itemId),
          }
        : {}),
      ...publishing,
      // The channel id is the platform until M7's channels arrive.
      setPostLink: async (channelId, url) => {
        const channel = data.channels.find((item) => item.id === channelId);
        if (!channel) return;
        await setLink.mutateAsync({ platform: channel.platform, url });
        await settle();
      },
      clearPostLink: async (channelId) => {
        const channel = data.channels.find((item) => item.id === channelId);
        if (!channel) return;
        await clearLink.mutateAsync(channel.platform);
        await settle();
      },
      refreshNumbers: (channelId) => {
        const channel = data.channels.find((item) => item.id === channelId);
        if (channel) refresh.mutate(channel.platform);
      },
    };
  }, [base, slotId, slotKind, local, distribution, timeZone, addFiles, drop, deleteFile, holdRemoval, update, saveCopy, setItems, setItemFile, setLink, clearLink, refresh, scheduleOne, unscheduleOne, retryOne, postNowOne, navigate, qc]);

  return handle;
}
